import { useEffect, useMemo, useState } from "react";
import { Phone, Loader2, RefreshCcw, Clock, CheckCircle2, PhoneIncoming, X } from "lucide-react";
import { voiceSummary, voiceCalls, voiceCallDetail } from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";

function fmtDuration(s) {
  if (!s && s !== 0) return "—";
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}m ${r}s`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusPill({ status, lastEvent }) {
  const ended = lastEvent === "end-of-call-report" || status === "ended";
  const label = ended ? "Completed" : status || lastEvent || "In progress";
  return (
    <span
      data-testid="voice-call-status"
      className={[
        "inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] uppercase tracking-[0.14em] font-semibold",
        ended ? "bg-[#ECFDF5] text-[#065F46]" : "bg-[#FEF3C7] text-[#92400E]",
      ].join(" ")}
    >
      {ended ? <CheckCircle2 size={11} /> : <PhoneIncoming size={11} />}
      {label}
    </span>
  );
}

export default function VoiceDashboard() {
  useSessionRenewal();
  const [summary, setSummary] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c] = await Promise.all([voiceSummary(), voiceCalls(100)]);
      setSummary(s);
      setCalls(c?.calls || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load voice calls.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCall = async (id) => {
    setActiveId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const d = await voiceCallDetail(id);
      setDetail(d);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load call.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeCall = () => {
    setActiveId(null);
    setDetail(null);
  };

  const stats = useMemo(() => ([
    { label: "Total calls", value: summary?.total_calls ?? 0, icon: Phone, testid: "voice-stat-total" },
    { label: "Completed", value: summary?.completed_calls ?? 0, icon: CheckCircle2, testid: "voice-stat-completed" },
    { label: "Last 24h", value: summary?.last_24h ?? 0, icon: Clock, testid: "voice-stat-recent" },
  ]), [summary]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="voice-dashboard">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">Voice</p>
            <h1 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight text-[#080808]">
              Inbound call assistant
            </h1>
            <p className="mt-3 text-[#3C4048] max-w-2xl">
              Live transcripts and summaries from calls answered by Ashlan&apos;s AI receptionist
              on <span className="font-semibold text-[#080808]">{summary?.twilio_number || "—"}</span>.
              Compliance-first: no diagnosis, soft outcome language, human handoff on request.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="voice-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#e5e7eb] text-[#080808] font-bold uppercase tracking-[0.12em] text-xs hover:border-[#3A7A8C]"
          >
            <RefreshCcw size={14} /> Refresh
          </button>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {stats.map(({ label, value, icon: Icon, testid }) => (
            <div
              key={label}
              data-testid={testid}
              className="bg-white border border-[#E5E7EB] p-5 flex items-center gap-4"
            >
              <div className="w-10 h-10 bg-[#F1F5F9] flex items-center justify-center">
                <Icon size={18} className="text-[#3A7A8C]" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6B7280]">{label}</p>
                <p className="mt-1 text-2xl font-bold text-[#080808]">{value}</p>
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div data-testid="voice-error" className="mb-6 p-3 border border-[#FCA5A5] bg-[#FEF2F2] text-[#7F1D1D] text-sm">
            {error}
          </div>
        )}

        {/* Calls table */}
        <div className="bg-white border border-[#E5E7EB]">
          <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-[#080808]">Recent calls</h2>
            {loading && <Loader2 size={14} className="animate-spin text-[#3A7A8C]" />}
          </div>
          {(!loading && calls.length === 0) ? (
            <div data-testid="voice-empty" className="p-10 text-center text-[#6B7280] text-sm">
              No calls yet. When someone calls {summary?.twilio_number || "the clinic"}, the
              transcript will appear here within a few seconds of the call ending.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] text-[11px] uppercase tracking-[0.18em] text-[#6B7280]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">When</th>
                    <th className="text-left px-4 py-3 font-semibold">Caller</th>
                    <th className="text-left px-4 py-3 font-semibold">Duration</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    <th className="text-left px-4 py-3 font-semibold">Summary</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c) => (
                    <tr
                      key={c.call_id}
                      data-testid={`voice-row-${c.call_id}`}
                      className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer"
                      onClick={() => openCall(c.call_id)}
                    >
                      <td className="px-4 py-3 text-[#3C4048] whitespace-nowrap">{fmtTime(c.last_event_at || c.started_at)}</td>
                      <td className="px-4 py-3 font-mono text-[#080808] whitespace-nowrap">{c.caller_number || "—"}</td>
                      <td className="px-4 py-3 text-[#3C4048] whitespace-nowrap">{fmtDuration(c.duration_seconds)}</td>
                      <td className="px-4 py-3"><StatusPill status={c.status} lastEvent={c.last_event} /></td>
                      <td className="px-4 py-3 text-[#3C4048] max-w-md truncate">{c.summary || (c.transcript ? c.transcript.slice(0, 120) + "…" : "—")}</td>
                      <td className="px-4 py-3 text-right text-[#3A7A8C] text-xs font-semibold uppercase tracking-[0.14em]">View</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {activeId && (
        <div
          data-testid="voice-detail-overlay"
          className="fixed inset-0 z-50 bg-[#080808]/40 flex justify-end"
          onClick={closeCall}
        >
          <div
            data-testid="voice-detail-panel"
            className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">Call</p>
                <h3 className="mt-1 text-lg font-bold text-[#080808] font-mono break-all">{activeId}</h3>
              </div>
              <button
                type="button"
                onClick={closeCall}
                data-testid="voice-detail-close"
                className="p-2 text-[#6B7280] hover:text-[#080808]"
              >
                <X size={18} />
              </button>
            </div>
            {detailLoading || !detail ? (
              <div className="p-10 flex justify-center">
                <Loader2 size={20} className="animate-spin text-[#3A7A8C]" />
              </div>
            ) : (
              <div className="px-6 py-5 space-y-6">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="Caller" value={detail.caller_number || "—"} mono />
                  <Field label="Duration" value={fmtDuration(detail.duration_seconds)} />
                  <Field label="Started" value={fmtTime(detail.started_at)} />
                  <Field label="Ended" value={fmtTime(detail.ended_at)} />
                  <Field label="Status" value={detail.status || detail.last_event || "—"} />
                  <Field label="Outcome" value={detail.outcome || detail.success_evaluation || "—"} />
                </div>

                {detail.summary && (
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6B7280] mb-2">AI summary</p>
                    <div
                      data-testid="voice-detail-summary"
                      className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 text-sm text-[#080808] whitespace-pre-wrap"
                    >
                      {detail.summary}
                    </div>
                  </div>
                )}

                {detail.transcript && (
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6B7280] mb-2">Transcript</p>
                    <div
                      data-testid="voice-detail-transcript"
                      className="bg-white border border-[#E5E7EB] p-4 text-sm text-[#080808] whitespace-pre-wrap font-mono leading-relaxed"
                    >
                      {detail.transcript}
                    </div>
                  </div>
                )}

                {Array.isArray(detail.timeline) && detail.timeline.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6B7280] mb-2">Event timeline</p>
                    <ul className="text-xs text-[#3C4048] space-y-1">
                      {detail.timeline.map((ev, i) => (
                        <li key={i} className="flex justify-between border-b border-[#F1F5F9] py-1">
                          <span>{ev.event}</span>
                          <span className="text-[#6B7280]">{fmtTime(ev.received_at)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6B7280]">{label}</p>
      <p className={["mt-1 text-[#080808]", mono ? "font-mono" : ""].join(" ")}>{value}</p>
    </div>
  );
}
